// src/App/App.js
import React, { useState, useMemo, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

import Login from '../features/Login/Login';
import ForgotPassword from '../features/ForgotPassword/ForgotPassword';
import Registered from '../features/Registered/Registered';
import HomePage from '../features/HomePage/HomePage';
import InvestmentProjects from '../features/InvestmentProjects/InvestmentProjects';
import ProjectDetails from '../features/ProjectDetails/ProjectDetails';
import Profile from '../features/Profile/Profile';
import ResetPassword from '../features/ResetPassword/ResetPassword';

import ProtectedRoute from '../components/ProtectedRoute';
// If you prefer to keep PublicRoute in a separate file, remove the inline one below and import it instead.
// import PublicRoute from '../components/PublicRoute';

import './App.css';
import createAppTheme from '../styles/theme'; // function that creates theme per mode
import { ColorModeContext } from '../styles/ColorModeContext';

// Auth & session tools
import { AuthProvider, AuthContext } from '../components/AuthContext/AuthContext';
import useSessionExpired from '../hooks/useSessionExpired';
import SessionExpiredPopup from '../components/SessionExpiredPopup/SessionExpiredPopup';

/**
 * Inline PublicRoute: redirects to /home when token exists.
 * Keeps behavior simple and avoids an extra file.
 */
const PublicRoute = ({ children }) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return token ? <Navigate to="/home" replace /> : children;
};

/**
 * Catch-all redirect component based on auth state.
 */
function CatchAllRedirect() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return token ? <Navigate to="/home" replace /> : <Navigate to="/" replace />;
}

/**
 * AppContent - actual routes and popup wiring.
 * Uses useSessionExpired hook (which installs axios interceptor)
 * and reads logout() from AuthContext to perform a clean logout when session expires.
 */
function AppContent() {
  // installs axios interceptor and exposes isSessionExpired flag
  const { isSessionExpired, setSessionExpired } = useSessionExpired();

  // get logout from context so popup can trigger a clean logout + redirect
  const { logout } = useContext(AuthContext);

  // handle popup close (hide only)
  const handleClosePopup = () => {
    setSessionExpired(false);
  };

  // when user clicks relogin on popup -> perform logout (clears tokens and navigates to login)
  const handleRelogin = () => {
    if (typeof logout === 'function') {
      logout();
    } else {
      // fallback: clear and hard navigate
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      window.location.replace('/');
    }
  };

  return (
    <>
      <SessionExpiredPopup
        isVisible={isSessionExpired}
        onClose={handleClosePopup}
        onRelogin={handleRelogin}
      />

      <Routes>
        {/* Public routes */}
        <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password/:token" element={<PublicRoute><ResetPassword /></PublicRoute>} />
        <Route path="/registered" element={<PublicRoute><Registered /></PublicRoute>} />

        {/* Protected routes */}
        <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute><InvestmentProjects /></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetails /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

        {/* Catch-all */}
        <Route path="*" element={<CatchAllRedirect />} />
      </Routes>
    </>
  );
}

/**
 * Full App component - theme + Router + AuthProvider wrapper.
 * Place AuthProvider inside Router so any future useNavigate inside AuthContext works.
 */
function App() {
  const [mode, setMode] = useState(() => localStorage.getItem('theme') || 'light');

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prev) => {
          const next = prev === 'light' ? 'dark' : 'light';
          localStorage.setItem('theme', next);
          return next;
        });
      },
    }),
    []
  );

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
